/**
 * Zod's refusals, in German — LV §128, §161, §196.
 *
 * Zod ships English messages ("Required", "String must contain at least 1
 * character(s)"). Every other refusal a user can see in this product is German, including
 * the audit diff's enum labels, which are resolved server-side precisely so no English
 * code reaches a screen (decision 052). A validation library is not an exception to that
 * rule just because the string comes from a dependency.
 *
 * This is a **display** concern and nothing else. The map is passed per parse rather than
 * installed with `z.setErrorMap`, because a global error map is module state: the order
 * tests import in would decide whether it is installed, which is the shape that already
 * cost this project a green suite proving nothing (decision 065, restating the
 * placeholder-secret episode — the first full run exited 0 and only the re-run caught it).
 */

import { z } from "zod";

/**
 * German text for the issue kinds these forms can actually produce.
 *
 * An issue that already carries a message keeps it — that is a `.refine()` in
 * `fields.ts` saying something specific, and it is always more useful than a generic
 * translation of the issue code.
 */
export const deutscheFehler: z.ZodErrorMap = (issue) => {
  if (issue.message) return { message: issue.message };

  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      // An absent value on a required field. Zod reports this as a type error because
      // `undefined` is not a string; the user's word for it is "Pflichtfeld".
      if (issue.received === "undefined" || issue.received === "null") {
        return { message: "Pflichtfeld." };
      }
      return { message: "Ungültiger Wert." };

    case z.ZodIssueCode.too_small:
      if (issue.type === "string") {
        return issue.minimum === 1
          ? { message: "Pflichtfeld." }
          : { message: `Mindestens ${issue.minimum} Zeichen.` };
      }
      return { message: `Mindestens ${issue.minimum}.` };

    case z.ZodIssueCode.too_big:
      if (issue.type === "string") {
        return { message: `Höchstens ${issue.maximum} Zeichen.` };
      }
      return { message: `Höchstens ${issue.maximum}.` };

    case z.ZodIssueCode.invalid_enum_value:
      return { message: "Bitte einen der angebotenen Werte wählen." };

    default:
      // Deliberately not `ctx.defaultError` — that is the English string this map exists
      // to keep off the screen. A kind of refusal not handled above is a prompt to add a
      // case here, not to leak the library's own wording.
      return { message: "Ungültige Eingabe." };
  }
};

/** Exported for the test that proves no English default can reach a field. */
export const GENERISCHE_MELDUNG = "Ungültige Eingabe.";
